import { AlertTriangle } from "lucide-react";

const TONE_CLASSES = {
  amber: "amber-text",
  green: "neon-text",
};

function StatCell({ label, value, tone, testId }) {
  return (
    <div data-testid={testId} className="p-5 border-r border-zinc-800 last:border-r-0">
      <div className="overline">{label}</div>
      <div className={`font-display text-3xl mt-2 ${TONE_CLASSES[tone] || "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function StatsBar({ result }) {
  const stats = [
    { label: "MAX SAFE STEPS", value: result.max_safe_steps, testId: "stat-safe-steps" },
    { label: "BUST RISK", value: `${result.bust_risk_pct}%`, tone: "amber", testId: "stat-bust-risk" },
    {
      label: "TOTAL NEEDED",
      value: `$${result.total_bankroll_required.toLocaleString()}`,
      testId: "stat-total-needed",
    },
    { label: "VARIANT", value: result.variant.toUpperCase(), tone: "green", testId: "stat-variant" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-800">
      {stats.map((s) => (
        <StatCell key={s.label} {...s} />
      ))}
    </div>
  );
}

function Warnings({ warnings }) {
  if (!warnings?.length) return null;
  return (
    <div className="border border-amber-500/40 bg-amber-500/5 p-5">
      <div className="flex items-center gap-2 overline amber-text mb-3">
        <AlertTriangle size={14} strokeWidth={1.5} /> WARNINGS
      </div>
      <ul className="space-y-2 text-sm text-amber-300" data-testid="calc-warnings">
        {warnings.map((w) => (
          <li key={w}>· {w}</li>
        ))}
      </ul>
    </div>
  );
}

function progressionRowClass(step, maxSafe) {
  if (step.exceeds_table_limit || step.bankroll_remaining_if_loss < 0) {
    return "bg-red-500/10";
  }
  if (step.step === maxSafe) return "bg-[#00D4FF]/5";
  return "";
}

function ProgressionTable({ result }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">PROGRESSION TABLE</div>
        <div className="mono text-xs text-zinc-500">
          base ${result.base_stake} · bankroll ${result.bankroll.toLocaleString()}
        </div>
      </div>
      <div className="border border-zinc-800 overflow-hidden">
        <table className="w-full mono text-sm" data-testid="progression-table">
          <thead className="bg-zinc-900">
            <tr className="text-left">
              <th className="px-4 py-3 overline">STEP</th>
              <th className="px-4 py-3 overline text-right">STAKE</th>
              <th className="px-4 py-3 overline text-right">CUMUL RISK</th>
              <th className="px-4 py-3 overline text-right">P/L IF WIN</th>
              <th className="px-4 py-3 overline text-right">BANKROLL AFTER LOSS</th>
            </tr>
          </thead>
          <tbody>
            {result.steps.map((s) => (
              <tr
                key={s.step}
                className={`border-t border-zinc-800 ${progressionRowClass(s, result.max_safe_steps)}`}
              >
                <td className="px-4 py-3 text-zinc-400">#{s.step}</td>
                <td className="px-4 py-3 text-right text-white">${s.stake.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-zinc-400">
                  ${s.cumulative_risked.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right neon-text">+${s.profit_if_win.toFixed(2)}</td>
                <td
                  className={`px-4 py-3 text-right ${
                    s.bankroll_remaining_if_loss < 0 ? "text-red-400" : "text-zinc-400"
                  }`}
                >
                  ${s.bankroll_remaining_if_loss.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CalculatorResult({ result }) {
  if (!result) return null;
  return (
    <div className="lg:col-span-8 space-y-6">
      <StatsBar result={result} />
      <Warnings warnings={result.warnings} />
      <ProgressionTable result={result} />
    </div>
  );
}
